import React, { useEffect, useRef, useState } from 'react';

import type { Leaves, Paths } from '@aline/common';
import {
  getISO8601DateString,
  NUMBER_FIELD_WARNING,
  shouldShowNumberFieldWarning,
} from '@aline/common';
import {
  Collapse,
  Divider,
  FormControlLabel,
  InputBase,
  List,
  ListItemButton,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { Field, Form, Formik, useFormikContext } from 'formik';
import type { FieldProps } from 'formik';
import { isHotkey } from 'is-hotkey';
import { ExternalLink, Lock, LockOpen, RotateCcw } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import { useSnackbar } from 'notistack';
import { flushSync } from 'react-dom';
import { AlineInput } from 'src/AlineInput';
import { AlineAvatar, AlineRadio, SearchInput } from 'src/components';
import { AiReasoningSection as SharedAiReasoningSection } from 'src/components/ai/shared/components/AiReasoningSection';
import { AlineCheckbox, AlineStaticDatePicker, AlineTooltip } from 'src/components/generic';
import { AIReasoningIcon } from 'src/components/icons/AIReasoningIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { ManualEntryIcon } from 'src/components/icons/ManualEntryIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import {
  getIsJobLocked,
  getLockedByTooltip,
  reportRowHeightPx,
  useLockConfirmation,
} from 'src/components/reports';
import type { ReportRowQuestionJob } from 'src/components/reports';
import { getHasAiReasoning, OptionItemChip } from 'src/editor';
import { Apollo } from 'src/gql/generated';
import { openDocumentQuestion } from 'src/helpers';
import { useCreateExtractionJobForDocument } from 'src/hooks';
import { Sentry } from 'src/sentry';
import type { DeepPartial } from 'ts-essentials';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

const POPOVER_ICON_SIZE = 18;

interface ReportCellPopoverProps {
  anchorEl: HTMLElement | null;
  documentId: UUID;
  extractionJob: DeepPartial<ReportRowQuestionJob>;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const useCellPopperStyles = makeStyles()(theme => ({
  checkbox: {
    padding: 0,
  },
  computedPaper: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 120px)',
    maxWidth: 480,
    minHeight: reportRowHeightPx,
    minWidth: 320,
    overflow: 'auto',
    position: 'relative',
    width: 'min-content',
  },
  containerPadding: {
    padding: theme.spacing(1.5),
  },
  emptyList: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    gap: theme.spacing(1),
    left: 0,
    position: 'absolute',
    top: -50,
    zIndex: theme.zIndex.tooltip + 1,
  },
  list: {
    maxHeight: 320,
    overflow: 'auto',
  },
  numberInput: {
    '&:focus-within': {
      border: 'none',
      borderRadius: 0,
    },
    border: 'none',
    borderRadius: 0,
    padding: theme.spacing(1.5),
  },
  numberInputBase: {
    padding: theme.spacing(0.5, 0),
  },
  popoverPaper: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    minWidth: 320,
    overflow: 'visible',
  },
  popperText: {
    color: theme.palette.text.primary,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
  },
  radioOption: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    margin: 0,
    padding: theme.spacing(1, 2),
  },
  radioSelected: {
    borderColor: theme.palette.primary.main,
  },
  reasoningCollapse: {
    '&.MuiCollapse-hidden': {
      minWidth: 0,
      width: 0,
    },
  },
  reasoningSection: {
    padding: theme.spacing(1.5),
  },
  refPaper: {
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    cursor: 'pointer',
  },
  searchInput: {
    '&:focus-within': {
      border: 'none',
    },
    border: 'none',
    minWidth: 'unset',
  },
  transparentBackground: {
    backgroundColor: 'transparent',
  },
}));

interface ReviewBadgeIconProps {
  extractionJob: DeepPartial<ReportRowQuestionJob>;
}

function ReviewBadgeIcon({ extractionJob }: ReviewBadgeIconProps): JSX.Element {
  const theme = useTheme();
  const review = extractionJob.review;

  if (review?.reviewAction === 'Edited') {
    const avatarColor = review.reviewedBy?.avatarColor || theme.palette.primary.main;

    return (
      <AlineAvatar
        avatarInfo={{
          __typename: 'User' as const,
          avatarColor,
          displayName: review.reviewedBy?.displayName || 'A user',
        }}
        styles={{ backgroundColor: avatarColor, size: 'xs' }}
      />
    );
  }

  return <QuestionModeIcon fill={IconColors.Primary} mode={extractionJob.question?.mode} />;
}

export function QuestionModeIcon({
  fill = IconColors.Primary,
  mode,
}: {
  fill?: string;
  mode?: Apollo.ExtractionQuestionMode;
}): JSX.Element {
  switch (mode) {
    case Apollo.ExtractionQuestionMode.Manual:
      return <ManualEntryIcon fill={fill} />;
    default:
      return <SmallAiSparkleIcon fill={fill} />;
  }
}

export const ReportCellPopover = React.memo(function ReportCellPopover({
  anchorEl,
  documentId,
  extractionJob,
  open,
  setOpen,
}: ReportCellPopoverProps): JSX.Element {
  const { classes } = useCellPopperStyles();
  const { enqueueSnackbar } = useSnackbar();
  const popperRef = useRef<HTMLDivElement | null>(null);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [revertExtractionJob] = Apollo.useRevertExtractionJobMutation();
  const hasReasoning = getHasAiReasoning({
    mode: extractionJob.question?.mode,
    reasoning: extractionJob.reasoning,
  });
  const [toggleExtractionJobsLock] = Apollo.useToggleExtractionJobsLockMutation();
  const hasPreviousAnswer = Boolean(
    typeof extractionJob.previousAnswer === 'string' ||
    extractionJob.previousSelectedOptions?.length
  );
  const isLocked = getIsJobLocked(extractionJob);
  const lockedTooltip = getLockedByTooltip(extractionJob);
  const { requestLockConfirmation } = useLockConfirmation();

  const getToolbarText = () => {
    switch (extractionJob.review?.reviewAction) {
      case 'Accepted':
        return `Accepted by ${extractionJob.review.reviewedBy?.displayName || 'a user'}`;
      case 'Rejected':
        return `Rejected by ${extractionJob.review.reviewedBy?.displayName || 'a user'}`;
      case 'Edited':
        return `Edited by ${extractionJob.review.reviewedBy?.displayName || 'a user'}`;
      default: {
        return extractionJob.question?.mode === Apollo.ExtractionQuestionMode.Manual
          ? 'Manual Entry'
          : 'Aline AI';
      }
    }
  };

  const handleRevert = async () => {
    if (!extractionJob.id || !extractionJob.question?.id) return;

    await revertExtractionJob({
      optimisticResponse: {
        __typename: 'Mutation',
        revertExtractionJob: {
          __typename: 'ExtractionJob',
          answer: extractionJob.previousAnswer ?? '',
          id: extractionJob.id,
          previousAnswer: null,
          previousSelectedOptions: [],
          review: null,
          selectedOptions:
            extractionJob.previousSelectedOptions?.map(o => ({
              __typename: 'QuestionOption',
              color: o?.color ?? '',
              id: o?.id ?? '',
              label: o?.label ?? '',
              order: o?.order ?? 0,
            })) ?? [],
        },
      },
      variables: {
        extractionJobId: extractionJob.id,
      },
    });
  };

  const handleLockToggle = () => {
    if (!extractionJob.id) return;
    const extractionJobId = extractionJob.id;
    const action = isLocked ? 'unlock' : 'lock';
    setOpen(false);
    requestLockConfirmation({
      action,
      onConfirm: async () => {
        try {
          await toggleExtractionJobsLock({
            /**
             * Cell lock affects the column's aggregate count — refetch every
             * mounted GetQuestionLockStatus so column headers stay accurate.
             */
            refetchQueries: [Apollo.namedOperations.Query.GetQuestionLockStatus],
            variables: {
              input: {
                extractionJobIds: [extractionJobId],
                isLocked: action === 'lock',
              },
            },
          });
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: 'report-cell-lock' } });
          enqueueSnackbar('Failed to update cell lock. Please try again.', { variant: 'error' });
        }
      },
      target: 'cell',
    });
  };

  function handleOpenDocumentReference() {
    flushSync(() => setOpen(false));
    openDocumentQuestion({ documentId, questionId: extractionJob.question?.id });
  }

  useEffect(() => {
    if (!open) return;

    function handleDocumentClick(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && popperRef.current?.contains(target)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setOpen(false);
    }

    function handleBackgroundScroll(event: WheelEvent | TouchEvent) {
      const target = event.target;
      if (target instanceof Node && popperRef.current?.contains(target)) return;

      event.preventDefault();
      event.stopPropagation();
    }

    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('wheel', handleBackgroundScroll, {
      capture: true,
      passive: false,
    });
    window.addEventListener('touchmove', handleBackgroundScroll, {
      capture: true,
      passive: false,
    });

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      window.removeEventListener('wheel', handleBackgroundScroll, { capture: true });
      window.removeEventListener('touchmove', handleBackgroundScroll, { capture: true });
    };
  }, [open, setOpen]);

  return (
    <>
      <Popper
        anchorEl={anchorEl}
        className={classes.popoverPaper}
        modifiers={[
          {
            enabled: true,
            name: 'flip',
            options: { boundary: 'viewport', padding: 8 },
          },
          {
            enabled: true,
            name: 'preventOverflow',
            options: { altAxis: true, boundary: 'viewport', padding: 8 },
          },
        ]}
        open={open}
        placement="bottom-start"
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      >
        <div
          ref={popperRef}
          className={classes.transparentBackground}
          onClick={e => e.stopPropagation()}
        >
          {open && (
            <div className={classes.header}>
              <Paper className={classes.computedPaper}>
                <ReviewBadgeIcon extractionJob={extractionJob} />
                <Typography className={classes.popperText}>{getToolbarText()}</Typography>
              </Paper>
              {hasPreviousAnswer && (
                <AlineTooltip arrow placement="top" title="Revert to original answer">
                  <Paper
                    className={clsx(classes.computedPaper, classes.refPaper)}
                    onClick={handleRevert}
                  >
                    <RotateCcw color={IconColors.DarkGrey} size={POPOVER_ICON_SIZE} />
                  </Paper>
                </AlineTooltip>
              )}
              {hasReasoning && (
                <AlineTooltip
                  /**
                   * MUI Tooltip can become detached from the anchor element when the title changes
                   * while hovered (frequent during AI runs that re-render the grid). This key forces
                   * a new Popper anchor to be created when the reasoning toggle state changes.
                   */
                  key={reasoningOpen ? 'reasoning-open' : 'reasoning-closed'}
                  arrow
                  placement="top"
                  title={reasoningOpen ? 'Hide AI reasoning' : 'Show AI reasoning'}
                >
                  <Paper
                    className={clsx(classes.computedPaper, classes.refPaper)}
                    onClick={() => setReasoningOpen(o => !o)}
                  >
                    <AIReasoningIcon height={POPOVER_ICON_SIZE} width={POPOVER_ICON_SIZE} />
                  </Paper>
                </AlineTooltip>
              )}
              <AlineTooltip
                /**
                 * MUI Tooltip can become detached from the anchor element when the icon/title changes while hovered.
                 * This key forces a new Popper anchor to be created when the lock state changes.
                 */
                key={isLocked ? 'locked' : 'unlocked'}
                arrow
                placement="top"
                title={isLocked ? `${lockedTooltip}. Click to unlock.` : 'Lock cell'}
              >
                <Paper
                  className={clsx(classes.computedPaper, classes.refPaper)}
                  onClick={handleLockToggle}
                >
                  {isLocked ? (
                    <LockOpen color={IconColors.DarkGrey} size={POPOVER_ICON_SIZE} />
                  ) : (
                    <Lock color={IconColors.DarkGrey} size={POPOVER_ICON_SIZE} />
                  )}
                </Paper>
              </AlineTooltip>
              <AlineTooltip arrow placement="top" title="View in document">
                <Paper
                  className={clsx(classes.computedPaper, classes.refPaper)}
                  onClick={handleOpenDocumentReference}
                >
                  <ExternalLink color={IconColors.DarkGrey} size={POPOVER_ICON_SIZE} />
                </Paper>
              </AlineTooltip>
            </div>
          )}
          <div
            className={clsx(
              classes.container,
              extractionJob.question?.type === Apollo.ExtractionQuestionType.Boolean &&
                classes.containerPadding
            )}
            onClick={e => e.stopPropagation()}
          >
            <ReportCellEditForm
              documentId={documentId}
              extractionJob={extractionJob}
              isLocked={isLocked}
              open={open}
              setOpen={setOpen}
            />
            <Collapse className={classes.reasoningCollapse} in={reasoningOpen && hasReasoning}>
              <AiReasoningSection extractionJob={extractionJob} />
            </Collapse>
          </div>
        </div>
      </Popper>
    </>
  );
});

interface ReportCellEditFormProps {
  documentId: UUID;
  extractionJob: DeepPartial<ReportRowQuestionJob>;
  isLocked: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
}

type ReportCellEditFormValues =
  | { __typename: 'StringAnswer'; answer: string }
  | { __typename: 'OptionAnswer'; selectedOptionIds: UUID[] };
const answerKey: Leaves<ReportCellEditFormValues> = 'answer';
const selectedOptionsKey: Paths<ReportCellEditFormValues> = 'selectedOptionIds';

function ReportCellEditForm({
  documentId,
  extractionJob,
  isLocked,
  open,
  setOpen,
}: ReportCellEditFormProps): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [updateExtractionJob] = Apollo.useUpdateExtractionJobMutation();
  const { createExtractionJob } = useCreateExtractionJobForDocument(documentId);
  const isOptionAnswer =
    extractionJob.question?.type === Apollo.ExtractionQuestionType.SingleSelect ||
    extractionJob.question?.type === Apollo.ExtractionQuestionType.MultiSelect;

  if (isLocked) {
    return (
      <div style={{ padding: 12, width: '100%' }}>
        {isOptionAnswer ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {extractionJob.selectedOptions?.map(option =>
              option ? <OptionItemChip key={option.id} option={option} /> : null
            )}
          </div>
        ) : (
          <Typography>{extractionJob.answer || '-'}</Typography>
        )}
      </div>
    );
  }

  return (
    <Formik<ReportCellEditFormValues>
      enableReinitialize={!open}
      initialValues={
        isOptionAnswer
          ? {
              __typename: 'OptionAnswer',
              selectedOptionIds:
                extractionJob.selectedOptions?.flatMap(o => {
                  return o?.id ? [o.id] : [];
                }) ?? [],
            }
          : { __typename: 'StringAnswer', answer: extractionJob.answer ?? '' }
      }
      onSubmit={async (values, { setSubmitting }) => {
        const questionId = extractionJob.question?.id;
        if (!questionId) return;

        setSubmitting(true);
        try {
          if (!extractionJob.id) {
            await createExtractionJob({
              variables: {
                documentId,
                input: {
                  props: {
                    answer: values.__typename === 'StringAnswer' ? values.answer : undefined,
                  },
                  questionId,
                  selectedOptionIds:
                    values.__typename === 'OptionAnswer' ? values.selectedOptionIds : undefined,
                },
              },
            });
            return;
          }

          switch (values.__typename) {
            case 'OptionAnswer': {
              await updateExtractionJob({
                variables: {
                  input: {
                    extractionJobId: extractionJob.id,
                    props: {
                      reviewAction: Apollo.ReviewAction.Edited,
                    },
                    selectedOptionIds: values.selectedOptionIds,
                  },
                },
              });
              break;
            }
            case 'StringAnswer': {
              await updateExtractionJob({
                variables: {
                  input: {
                    extractionJobId: extractionJob.id,
                    props: {
                      answer: values.answer,
                      reviewAction: Apollo.ReviewAction.Edited,
                    },
                  },
                },
              });
              break;
            }
          }
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: 'report-cell-edit' } });
          enqueueSnackbar('Failed to save cell. Please try again.', { variant: 'error' });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <FormContent
        extractionQuestionType={extractionJob.question?.type ?? Apollo.ExtractionQuestionType.Text}
        questionId={extractionJob.question?.id ?? null}
        setOpen={setOpen}
      />
    </Formik>
  );
}

interface EditInputProps {
  questionId: UUID | null;
  type: Apollo.ExtractionQuestionType;
}

const EditInput = React.memo(function EditInput({ questionId, type }: EditInputProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useCellPopperStyles();
  const [search, setSearch] = useState<string>('');
  const { submitForm } = useFormikContext<{ answer: string }>();
  const debouncedSubmitForm = useDebouncedCallback(submitForm, 500);
  const { data } = Apollo.useGetExtractionQuestionWithOptionsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !questionId,
    variables: { id: questionId ?? '' },
  });

  switch (type) {
    case Apollo.ExtractionQuestionType.Text:
      return (
        <Field name={answerKey}>
          {({ field, form: { setFieldValue } }: FieldProps) => (
            <InputBase
              multiline
              inputProps={{ maxRows: 20 }}
              placeholder="Enter text..."
              style={{ fontWeight: 500, minHeight: 52, padding: theme.spacing(1.5), width: '100%' }}
              value={field.value}
              onChange={e => {
                setFieldValue(answerKey, e.target.value);
                debouncedSubmitForm();
              }}
            />
          )}
        </Field>
      );
    case Apollo.ExtractionQuestionType.Number:
      return (
        <Field name={answerKey}>
          {({ field, form: { setFieldValue } }: FieldProps) => (
            <AlineInput
              classes={{ input: classes.numberInputBase }}
              helperText={NUMBER_FIELD_WARNING}
              hideHelperText={!shouldShowNumberFieldWarning(field.value)}
              inputClassName={classes.numberInput}
              placeholder="Enter number..."
              value={field.value}
              onChange={e => {
                const newValue = e.target.value;
                if (newValue === field.value) return;

                setFieldValue(answerKey, newValue);
                debouncedSubmitForm();
              }}
            />
          )}
        </Field>
      );
    case Apollo.ExtractionQuestionType.Date:
      return (
        <Field name={answerKey}>
          {({ field, form: { setFieldValue } }: FieldProps) => {
            return (
              <AlineStaticDatePicker
                className={classes.transparentBackground}
                pickerProps={{
                  onChange: async newDate => {
                    setFieldValue(answerKey, newDate ? getISO8601DateString(newDate) : '');
                    await submitForm();
                  },
                  value: typeof field.value === 'string' ? parseISO(field.value) : null,
                }}
              />
            );
          }}
        </Field>
      );
    case Apollo.ExtractionQuestionType.Boolean:
      return (
        <Field name={answerKey}>
          {({ field, form: { setFieldValue } }: FieldProps) => {
            return (
              <div className={classes.radioGroup}>
                <FormControlLabel
                  className={clsx(
                    classes.radioOption,
                    field.value === 'true' && classes.radioSelected
                  )}
                  control={
                    <AlineRadio
                      checked={field.value === 'true'}
                      onChange={async () => {
                        setFieldValue(answerKey, 'true');
                        await submitForm();
                      }}
                    />
                  }
                  label="Yes"
                  value="true"
                />
                <FormControlLabel
                  className={clsx(
                    classes.radioOption,
                    field.value === 'false' && classes.radioSelected
                  )}
                  control={
                    <AlineRadio
                      checked={field.value === 'false'}
                      onChange={async () => {
                        setFieldValue(answerKey, 'false');
                        await submitForm();
                      }}
                    />
                  }
                  label="No"
                  value="false"
                />
              </div>
            );
          }}
        </Field>
      );
    case Apollo.ExtractionQuestionType.SingleSelect:
    case Apollo.ExtractionQuestionType.MultiSelect:
      return (
        <Field name={selectedOptionsKey}>
          {({ field, form: { setFieldValue } }: FieldProps<UUID[]>) => {
            const filteredOptions = matchSorter(data?.extractionQuestion?.options ?? [], search, {
              keys: ['label' satisfies keyof Pick<Apollo.QuestionOption, 'label'>],
            });

            return (
              <>
                <SearchInput
                  inputClassName={classes.searchInput}
                  placeholder="Search options..."
                  search={search}
                  setSearch={setSearch}
                  style={{ minWidth: 'unset' }}
                />
                <Divider />
                <OptionsList
                  options={filteredOptions}
                  selectedOptionIds={field.value}
                  onClick={async option => {
                    const newSelectedOptionIds =
                      type === Apollo.ExtractionQuestionType.SingleSelect
                        ? !field.value.includes(option.id)
                          ? [option.id]
                          : []
                        : field.value.includes(option.id)
                          ? field.value.filter(id => id !== option.id)
                          : [...field.value, option.id];
                    setFieldValue(selectedOptionsKey, newSelectedOptionIds);
                    await submitForm();
                  }}
                />
              </>
            );
          }}
        </Field>
      );
  }
});

function OptionsList({
  onClick,
  options,
  selectedOptionIds,
}: {
  onClick: (option: Pick<Apollo.QuestionOption, 'id' | '__typename'>) => void;
  options: Pick<Apollo.QuestionOption, 'id' | '__typename'>[];
  selectedOptionIds: UUID[];
}): JSX.Element {
  const theme = useTheme();
  const { classes } = useCellPopperStyles();

  if (!options.length)
    return (
      <div className={classes.emptyList}>
        <Typography align="center" variant="subtitle2">
          No options found.
        </Typography>
      </div>
    );

  return (
    <List dense className={classes.list}>
      {options.map(option => (
        <ListItemButton
          key={option.id}
          style={{ gap: theme.spacing(1), padding: theme.spacing(1, 2) }}
          onClick={async () => onClick(option)}
        >
          <AlineCheckbox
            checked={selectedOptionIds.includes(option.id)}
            classes={{ root: classes.checkbox }}
            tabIndex={-1}
          />
          <OptionItemChip option={option} />
        </ListItemButton>
      ))}
    </List>
  );
}

interface AiReasoningSectionProps {
  extractionJob: DeepPartial<ReportRowQuestionJob>;
}

function AiReasoningSection({ extractionJob }: AiReasoningSectionProps): JSX.Element | null {
  const { classes } = useCellPopperStyles();

  if (!extractionJob.reasoning) return null;

  return (
    <>
      <Divider />
      <SharedAiReasoningSection
        className={classes.reasoningSection}
        reasoning={extractionJob.reasoning}
      />
    </>
  );
}

const useFormStyles = makeStyles()(_theme => ({
  form: {
    width: '100%',
  },
}));

interface FormContentProps {
  extractionQuestionType: Apollo.ExtractionQuestionType;
  questionId: UUID | null;
  setOpen: (open: boolean) => void;
}

function FormContent({
  extractionQuestionType,
  questionId,
  setOpen,
}: FormContentProps): JSX.Element {
  const { classes } = useFormStyles();

  return (
    <Form
      className={classes.form}
      onKeyDown={async e => {
        if (isHotkey('escape', e.nativeEvent) || isHotkey('mod+enter', e.nativeEvent)) {
          e.preventDefault();
          setOpen(false);
        }
      }}
    >
      <EditInput questionId={questionId} type={extractionQuestionType} />
    </Form>
  );
}
